// Stable per-row identifier used as the external key when records are sent
// to Salesforce. Every row in every table-style array carries a `_uid`.

export function genUid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'uid-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

export function withUid(row) {
  if (row && typeof row === 'object' && !row._uid) {
    return { ...row, _uid: genUid() };
  }
  return row;
}

// Recursively walk loaded state and stamp `_uid` on any object element of
// any array. Used once at load time to migrate older saved data.
//
// An array nested inside an array is walked but never stamped, and that
// exclusion is load bearing rather than tidiness. `typeof [] === 'object'`, so
// without it a coordinate pair `[x, y]` was spread into `{0: x, 1: y, _uid}`
// and stopped being a point. Every whiteboard stroke is an array of such pairs,
// which meant an advisor's drawing was destroyed by the next reload: the ink
// was still in storage, and nothing could read it back. A `_uid` identifies a
// row in a table, and a row is always an object, so an array element that is
// itself an array is never something this should stamp.
export function ensureUidsDeep(value) {
  if (Array.isArray(value)) {
    return value.map(item => {
      if (Array.isArray(item)) return ensureUidsDeep(item);
      if (item && typeof item === 'object') {
        const stamped = item._uid ? item : { ...item, _uid: genUid() };
        return ensureUidsDeep(stamped);
      }
      return item;
    });
  }
  if (value && typeof value === 'object') {
    const next = {};
    for (const k of Object.keys(value)) next[k] = ensureUidsDeep(value[k]);
    return next;
  }
  return value;
}
